<template>
  <div class="workload-stats">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>工作量统计</span>
          <div class="header-controls" style="display: flex; align-items: center; gap: 20px;">
            <div class="time-unit-control" style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 13px; color: #94a3b8;">时长单位：</span>
              <el-switch
                v-model="isMinutesLocal"
                active-text="分钟"
                inactive-text="小时"
                inline-prompt
                style="--el-switch-on-color: #667eea; --el-switch-off-color: #94a3b8;"
                @change="handleTimeUnitChange"
              />
            </div>
            <el-select v-model="selectedSemesterId" placeholder="选择学期" @change="handleSemesterChange" style="width: 180px;">
              <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-button type="primary" @click="showImportDialog" :icon="Upload">
              导入历史监考
            </el-button>
          </div>
        </div>
      </template>

      <el-alert
        title="时长计算规则：按实际考试时长及配置折算；默认巡考不计入时长，可在排考设置中单独配置巡考系数。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />

      <el-table :data="stats" v-loading="loading" stripe>
        <el-table-column type="index" label="#" width="60" />
        <el-table-column prop="teacher_name" label="教师姓名" width="120" />
        <el-table-column prop="total_sessions" label="监考场次" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ row.total_sessions }}</span> 场
          </template>
        </el-table-column>
        <el-table-column prop="total_hours" label="监考时长" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ isMinutes ? (row.total_hours * 60).toFixed(0) : row.total_hours.toFixed(2) }}</span> {{ isMinutes ? '分钟' : '小时' }}
          </template>
        </el-table-column>
        <el-table-column label="工作量分布" min-width="300">
          <template #default="{ row }">
            <el-progress :percentage="getPercentage(row.total_hours)" :stroke-width="18"
              :color="getProgressColor(row.total_hours)">
              <span class="progress-text">{{ isMinutes ? (row.total_hours * 60).toFixed(0) + ' 分钟' : row.total_hours.toFixed(2) + ' 小时' }}</span>
            </el-progress>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && stats.length === 0" class="empty-state">
        <el-empty description="暂无统计数据" />
      </div>
    </el-card>

    <!-- Summary Card -->
    <el-row :gutter="20" style="margin-top: 20px" v-if="stats.length > 0">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #667eea, #764ba2)">
            <el-icon :size="28">
              <User />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">参与监考教师</div>
            <div class="stat-number">{{ stats.length }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #f093fb, #f5576c)">
            <el-icon :size="28">
              <Calendar />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">总监考场次</div>
            <div class="stat-number">{{ totalSessions }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #4facfe, #00f2fe)">
            <el-icon :size="28">
              <Clock />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">总监考时长</div>
            <div class="stat-number">{{ isMinutes ? (totalHours * 60).toFixed(0) + '分钟' : totalHours.toFixed(2) + 'h' }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #43e97b, #38f9d7)">
            <el-icon :size="28">
              <TrendCharts />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">人均时长</div>
            <div class="stat-number">{{ isMinutes ? (avgHours * 60).toFixed(0) + '分钟' : avgHours.toFixed(2) + 'h' }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Historical Workload Section -->
    <el-card style="margin-top: 20px" v-if="historicalRecords.length > 0">
      <template #header>
        <div class="card-header">
          <span>历史监考记录</span>
          <el-button type="danger" size="small" @click="clearAllHistorical" :icon="Delete">
            清空全部
          </el-button>
        </div>
      </template>
      <el-table :data="historicalRecords" stripe>
        <el-table-column type="index" label="#" width="60" />
        <el-table-column prop="teacher_name" label="教师姓名" width="120" />
        <el-table-column prop="total_sessions" label="导入场次" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ row.total_sessions }}</span> 场
          </template>
        </el-table-column>
        <el-table-column prop="total_hours" label="导入时长" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ isMinutes ? (row.total_hours * 60).toFixed(0) : row.total_hours.toFixed(2) }}</span> {{ isMinutes ? '分钟' : '小时' }}
          </template>
        </el-table-column>
        <el-table-column prop="source" label="导入来源" min-width="150" />
        <el-table-column label="操作" width="100">
          <template #default="{ row }">
            <el-button type="danger" size="small" link @click="deleteHistoricalRecord(row.id)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Import Dialog -->
    <el-dialog
      v-model="importDialogVisible"
      title="导入历史监考时长"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-alert
        title="导入规则"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #default>
          <p>导入后会覆盖同一教师在当前学期的历史监考记录，并参与后续自动排监考均衡。</p>
          <p style="margin-top: 8px;">Excel 与粘贴导入使用同一套格式，可带表头，也可直接从第一行开始填数据。</p>
        </template>
      </el-alert>

      <el-form :model="importForm" label-width="100px">
        <el-form-item label="数据格式">
          <el-radio-group v-model="importForm.importFormat">
            <el-radio-button label="name_hours">姓名 + 时长</el-radio-button>
            <el-radio-button label="name_sessions_hours">姓名 + 次数 + 时长</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="时长单位">
          <el-radio-group v-model="importForm.hoursUnit">
            <el-radio-button label="hours">小时</el-radio-button>
            <el-radio-button label="minutes">分钟</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="导入来源">
          <el-input 
            v-model="importForm.source" 
            placeholder="例如：2024年春季学期期中考试监考记录"
            clearable
          />
        </el-form-item>
      </el-form>

      <el-tabs v-model="importMode" type="border-card">
        <el-tab-pane label="Excel 导入" name="excel">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :on-exceed="handleExceed"
          >
            <el-button type="primary" :icon="Upload">选择 Excel 文件</el-button>
            <template #tip>
              <div class="el-upload__tip">
                只能上传 .xlsx 文件；列顺序需与上方“数据格式”一致。
              </div>
            </template>
          </el-upload>
        </el-tab-pane>
        <el-tab-pane label="粘贴导入" name="text">
          <el-input
            v-model="importText"
            type="textarea"
            :rows="8"
            :placeholder="importTextPlaceholder"
          />
          <div class="import-tip">
            支持用空格、Tab、逗号、顿号、竖线或分号分隔；可从 Excel 复制后直接粘贴。
          </div>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="importDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitImport" :loading="importing">
            导入
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { User, Calendar, Clock, TrendCharts, Upload, Delete } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { semesterApi, scheduleApi, authApi } from '@/api'
import { getUser, setUser } from '@/utils/auth'

const isMinutes = computed(() => getUser()?.use_minutes_format || false)
const isMinutesLocal = ref(getUser()?.use_minutes_format || false)

const handleTimeUnitChange = async (val) => {
  try {
    const payload = {
      use_minutes_format: val
    }
    const updatedUser = await authApi.updateProfile(payload)
    setUser(updatedUser)
    ElMessage.success(`显示单位已切换为 ${val ? '分钟' : '小时'}`)
    window.location.reload()
  } catch (e) {
    console.error(e)
    ElMessage.error('切换时间单位失败')
    isMinutesLocal.value = !val
  }
}

const semesters = ref([])
const selectedSemesterId = ref(null)
const stats = ref([])
const loading = ref(false)
const historicalRecords = ref([])

const totalSessions = computed(() =>
  stats.value.reduce((sum, s) => sum + s.total_sessions, 0)
)

const totalHours = computed(() =>
  stats.value.reduce((sum, s) => sum + s.total_hours, 0)
)

const avgHours = computed(() =>
  stats.value.length > 0 ? totalHours.value / stats.value.length : 0
)

const maxHours = computed(() =>
  Math.max(...stats.value.map(s => s.total_hours), 1)
)

const getPercentage = (hours) => {
  return Math.round((hours / maxHours.value) * 100)
}

const getProgressColor = (hours) => {
  const avg = avgHours.value
  if (hours > avg * 1.3) return '#f56c6c'  // Too high
  if (hours < avg * 0.7) return '#e6a23c'  // Too low
  return '#67c23a'  // Normal
}

const fetchSemesters = async () => {
  try {
    semesters.value = await semesterApi.getAll()
    if (semesters.value.length > 0) {
      const active = semesters.value.find(s => s.is_active)
      selectedSemesterId.value = active?.id || semesters.value[0].id
      await fetchStats()
      await fetchHistoricalRecords()
    } else {
      selectedSemesterId.value = null
      stats.value = []
      historicalRecords.value = []
    }
  } catch (e) {
    console.error(e)
  }
}

const fetchStats = async () => {
  if (!selectedSemesterId.value) return
  loading.value = true
  try {
    stats.value = await scheduleApi.getSemesterStats(selectedSemesterId.value)
    // Sort by hours descending
    stats.value.sort((a, b) => b.total_hours - a.total_hours)
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const handleSemesterChange = () => {
  fetchStats()
  fetchHistoricalRecords()
}

const fetchHistoricalRecords = async () => {
  if (!selectedSemesterId.value) return
  try {
    historicalRecords.value = await scheduleApi.getHistoricalWorkload(selectedSemesterId.value)
  } catch (e) {
    console.error(e)
    historicalRecords.value = []
    const detail = e.response?.data?.detail
    if (detail && detail !== '学期不存在') {
      ElMessage.warning(detail || '历史监考记录加载失败')
    }
  }
}

// Import functionality
const importDialogVisible = ref(false)
const importing = ref(false)
const importMode = ref('excel')
const importForm = ref({
  source: '',
  importFormat: 'name_sessions_hours',
  hoursUnit: 'hours'
})
const importFile = ref(null)
const importText = ref('')
const uploadRef = ref(null)

const importTextPlaceholder = computed(() => {
  if (importForm.value.importFormat === 'name_hours') {
    return importForm.value.hoursUnit === 'minutes'
      ? '姓名 监考时长(分钟)\n张三 300\n李四 180'
      : '姓名 监考时长(小时)\n张三 5\n李四 3'
  }
  return importForm.value.hoursUnit === 'minutes'
    ? '姓名 监考次数 监考时长(分钟)\n张三 5 300\n李四 3 180'
    : '姓名 监考次数 监考时长(小时)\n张三 5 12.5\n李四 3 8'
})

const showImportDialog = () => {
  importForm.value.source = ''
  importForm.value.importFormat = 'name_sessions_hours'
  importForm.value.hoursUnit = isMinutes.value ? 'minutes' : 'hours'
  importMode.value = 'excel'
  importFile.value = null
  importText.value = ''
  importDialogVisible.value = true
  if (uploadRef.value) {
    uploadRef.value.clearFiles()
  }
}

const handleFileChange = (file) => {
  importFile.value = file.raw
}

const handleFileRemove = () => {
  importFile.value = null
}

const handleExceed = () => {
  ElMessage.warning('只能选择一个文件')
}

const submitImport = async () => {
  if (importMode.value === 'excel' && !importFile.value) {
    ElMessage.warning('请选择要导入的 Excel 文件')
    return
  }

  if (importMode.value === 'text' && !importText.value.trim()) {
    ElMessage.warning('请粘贴要导入的数据')
    return
  }
  
  if (!selectedSemesterId.value) {
    ElMessage.warning('请先选择学期')
    return
  }
  
  importing.value = true
  try {
    const options = {
      import_format: importForm.value.importFormat,
      hours_unit: importForm.value.hoursUnit
    }
    const result = importMode.value === 'excel'
      ? await scheduleApi.importHistoricalWorkload(
          selectedSemesterId.value,
          importFile.value,
          importForm.value.source || null,
          options
        )
      : await scheduleApi.importHistoricalWorkloadText(selectedSemesterId.value, {
          text: importText.value,
          source: importForm.value.source || null,
          import_format: importForm.value.importFormat,
          hours_unit: importForm.value.hoursUnit
        })
    
    ElMessage.success(result.message || '导入成功')
    importDialogVisible.value = false
    
    // Refresh data
    fetchStats()
    fetchHistoricalRecords()
  } catch (e) {
    console.error(e)
    ElMessage.error(e.response?.data?.detail || '导入失败')
  } finally {
    importing.value = false
  }
}

const deleteHistoricalRecord = async (recordId) => {
  try {
    await ElMessageBox.confirm('确定要删除这条历史监考记录吗？', '确认删除', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    await scheduleApi.deleteHistoricalWorkload(recordId)
    ElMessage.success('删除成功')
    
    // Refresh data
    fetchStats()
    fetchHistoricalRecords()
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      ElMessage.error('删除失败')
    }
  }
}

const clearAllHistorical = async () => {
  try {
    await ElMessageBox.confirm('确定要清空所有历史监考记录吗？此操作不可恢复！', '确认清空', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // Delete all historical records for this semester
    for (const record of historicalRecords.value) {
      await scheduleApi.deleteHistoricalWorkload(record.id)
    }
    
    ElMessage.success('清空成功')
    
    // Refresh data
    fetchStats()
    fetchHistoricalRecords()
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      ElMessage.error('清空失败')
    }
  }
}

onMounted(fetchSemesters)
</script>

<style scoped>
.workload-stats {
  max-width: 1200px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.stat-value {
  font-weight: 600;
  color: #667eea;
}

.progress-text {
  font-size: 12px;
  color: #e2e8f0;
}

.empty-state {
  padding: 40px 0;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stat-card :deep(.el-card__body) {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-label {
  color: #a0aec0;
  font-size: 13px;
  margin-bottom: 4px;
}

.stat-number {
  color: #e2e8f0;
  font-size: 24px;
  font-weight: 600;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.import-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
</style>
